import React, { useState } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import Divider from "@mui/material/Divider";
import CloseIcon from "@mui/icons-material/Close";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import isapLogo from "../../assets/isap2027-logo.png";

const menuItems = [
  { id: "home", label: "Home", target: "Home" },
  {
    id: "important-dates",
    label: "Important Dates",
    target: "Important Dates",
  },
  {
    id: "organizing-committee",
    label: "Organizing Committee",
    target: "Organizing Committee",
  },
  {
    id: "technical-co-sponsors",
    label: "Technical Co-Sponsors",
    target: "Technical Co-Sponsors",
  },
  {
    id: "conference-topics",
    label: "Conference Topics",
    target: "Conference Topics",
  },
  { id: "venue", label: "Venue", target: "Venue" },
  { id: "about-isap", label: "About ISAP", target: "About ISAP" },
];

/**
 
 * @param {object} props 
 * @param {(path: string) => void} props.onNavigate
 * @param {string} props.activeSection
 */
function NavBar({ onNavigate, activeSection = "home" }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleNavigate = (target) => {
    onNavigate(target);
    if (drawerOpen) {
      setDrawerOpen(false);
    }
  };

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const mobileDrawerContent = (
    <Box
      sx={{ width: 300 }}
      role="presentation"
      onKeyDown={toggleDrawer(false)}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <img
            src={isapLogo}
            alt="ISAP 2027 Logo"
            style={{ height: "32px", filter: "brightness(1.1)" }}
          />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            ISAP2027
          </Typography>
        </Box>
        <IconButton onClick={toggleDrawer(false)} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />

      <List>
        {menuItems.map((item) => (
          <ListItemButton
            key={item.id}
            selected={activeSection === item.id}
            onClick={() => handleNavigate(item.target)}
            sx={{
              mx: 1,
              mt: 0.5,
              borderRadius: 2,
              "&.Mui-selected": {
                bgcolor: "rgba(74, 141, 176, 0.16)",
                color: "#1e4256",
                "& .MuiListItemText-primary": {
                  fontWeight: 700,
                },
              },
            }}
          >
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontWeight: 600,
              }}
            />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );

  return (
    <AppBar
      position="sticky"
      sx={{
        background: "linear-gradient(135deg, #2c5f7c 0%, #4a8db0 100%)",
        color: "white",
        boxShadow: "0 2px 12px rgba(44, 95, 124, 0.3)",
        borderRadius: 0,
        "& .MuiToolbar-root": {
          borderRadius: 0,
        },
      }}
    >
      <Toolbar
        sx={{
          borderRadius: 0,
          minHeight: { xs: 64, md: 76 },
          px: { xs: 2, md: 3 },
          gap: { md: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            flexShrink: 0,
          }}
          onClick={() => handleNavigate("Home")}
        >
          <img
            src={isapLogo}
            alt="ISAP 2027 Logo"
            style={{
              height: "40px",
              marginRight: "12px",
              filter: "brightness(1.1)",
            }}
          />
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: "bold",
              color: "white",
            }}
          >
            ISAP2027
          </Typography>
        </Box>

        {/* Mobile menu button */}
        {isMobile && (
          <IconButton
            size="large"
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer(true)}
            sx={{ color: "white" }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {!isMobile && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              ml: "clamp(8px, 2vw, 32px)",
            }}
          >
            {menuItems.map((item) => (
              <Button
                key={item.id}
                color="inherit"
                onClick={() => handleNavigate(item.target)}
                sx={{
                  color: "white",
                  px: 1.8,
                  py: 0.9,
                  minHeight: 42,
                  whiteSpace: "nowrap",
                  borderRadius: 999,
                  backgroundColor:
                    activeSection === item.id
                      ? "rgba(255, 255, 255, 0.2)"
                      : "transparent",
                  border:
                    activeSection === item.id
                      ? "1px solid rgba(255,255,255,0.35)"
                      : "1px solid transparent",
                  "&:hover": {
                    backgroundColor:
                      activeSection === item.id
                        ? "rgba(255, 255, 255, 0.24)"
                        : "rgba(255, 255, 255, 0.1)",
                    transform: "scale(1.02)",
                    transition:
                      "transform 0.2s ease-in-out, background-color 0.2s ease-in-out",
                  },
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>
        )}

        {/* Mobile Drawer */}
        <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
          {mobileDrawerContent}
        </Drawer>
      </Toolbar>
    </AppBar>
  );
}

export default NavBar;
